import { useState, useEffect } from 'react';
import {
  ModelSignupFormState,
  PhotoUpload,
  Reference,
  MODELING_STYLE_OPTIONS,
  MODEL_PHOTO_MIN_LONG_EDGE,
  MODEL_PHOTO_MIN_SHORT_EDGE,
  REQUIRED_MODEL_PHOTOS,
  REQUIRED_SPACE_PHOTOS,
  SPACE_PHOTO_MIN_LONG_EDGE,
  SPACE_PHOTO_MIN_SHORT_EDGE,
} from './modelSignup.types';
import { PhotoUploader } from './PhotoUploader';
import { LifeModelInfoModal } from './LifeModelInfoModal';
import { COUNTRIES } from './countries';
import { LANGUAGES, type ModelingStyle, type LanguageCode } from '@modelcast/types';
import { getDisplayNameValidationMessage } from '../../shared/validation/displayName';

const LANGUAGE_OPTIONS = Object.entries(LANGUAGES) as [LanguageCode, string][];

interface ModelSignupFormProps {
  formState: ModelSignupFormState;
  onUpdate: (updates: Partial<ModelSignupFormState>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

interface ValidationErrors {
  name?: string;
  username?: string;
  photos?: string;
  spacePhotos?: string;
  instagram?: string;
  website?: string;
  modelingPrefs?: string;
}

export function ModelSignupForm({
  formState,
  onUpdate,
  onSubmit,
  onCancel,
  isSubmitting,
}: ModelSignupFormProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [hasAttemptedNext, setHasAttemptedNext] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showLifeModelInfo, setShowLifeModelInfo] = useState(false);
  const [bioLength, setBioLength] = useState(formState.bio.length);
  const [experienceLength, setExperienceLength] = useState(formState.experience.length);
  const [cameraEquipmentLength, setCameraEquipmentLength] = useState(formState.cameraEquipment.length);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');

  const validateStep1 = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    const nameError = getDisplayNameValidationMessage(formState.name);
    if (nameError) {
      newErrors.name = nameError;
    }

    // Username validation
    if (!formState.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-z0-9_]{3,30}$/.test(formState.username)) {
      newErrors.username = 'Username must be 3-30 characters: lowercase letters, numbers, underscores only';
    } else if (usernameAvailable === false) {
      newErrors.username = 'This username is already taken';
    }

    if (formState.photos.length < REQUIRED_MODEL_PHOTOS) {
      newErrors.photos = `Please upload at least ${REQUIRED_MODEL_PHOTOS} high-resolution model photos`;
    }

    if (formState.spacePhotos.length < REQUIRED_SPACE_PHOTOS) {
      newErrors.spacePhotos = 'Please upload one photo of your modeling setup';
    }

    // Validate Instagram handle format (optional, but must be valid if provided)
    if (formState.instagram) {
      // Remove @ if present and check for valid characters
      const handle = formState.instagram.replace(/^@/, '');
      if (!/^[a-zA-Z0-9._]{1,30}$/.test(handle)) {
        newErrors.instagram = 'Enter a valid Instagram handle (letters, numbers, periods, underscores)';
      }
    }

    if (formState.website) {
      try {
        const value = formState.website.match(/^https?:\/\//i)
          ? formState.website
          : `https://${formState.website}`;
        new URL(value);
      } catch {
        newErrors.website = 'Enter a valid website URL';
      }
    }

    // At least one modeling style must be selected
    if (formState.modelingStyles.length === 0) {
      newErrors.modelingPrefs = 'Please select at least one modeling style';
    }

    return newErrors;
  };

  const validateStep2 = (): ValidationErrors => {
    return {};
  };

  // Check username availability with debounce
  useEffect(() => {
    const username = formState.username.toLowerCase();

    // Reset if empty or invalid format
    if (!username || !/^[a-z0-9_]{3,30}$/.test(username)) {
      setUsernameAvailable(null);
      return;
    }

    setCheckingUsername(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(`/api/profiles/username-available/${username}`);
        if (response.ok) {
          const data = await response.json();
          setUsernameAvailable(data.available);
        }
      } catch (error) {
        console.error('Error checking username:', error);
      } finally {
        setCheckingUsername(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formState.username]);

  // Re-validate when form changes after first next attempt
  useEffect(() => {
    if (hasAttemptedNext && step === 1) {
      setErrors(validateStep1());
    }
  }, [formState.name, formState.username, formState.photos, formState.spacePhotos, formState.instagram, formState.website, formState.modelingStyles, hasAttemptedNext, step, usernameAvailable]);

  useEffect(() => {
    if (hasAttemptedSubmit && step === 2) {
      setErrors(validateStep2());
    }
  }, [formState.spacePhotos, hasAttemptedSubmit, step]);

  const handleNext = () => {
    setHasAttemptedNext(true);
    const validationErrors = validateStep1();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setStep(1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = () => {
    setHasAttemptedSubmit(true);
    const validationErrors = validateStep2();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      onSubmit();
    }
  };

  const handlePhotosChange = (photos: PhotoUpload[]) => {
    onUpdate({ photos });
  };

  const handleVerificationPhotosChange = (verificationPhotos: PhotoUpload[]) => {
    onUpdate({ verificationPhotos });
  };

  const handleSpacePhotosChange = (spacePhotos: PhotoUpload[]) => {
    onUpdate({ spacePhotos });
  };

  // Format Instagram handle - add @ if not present
  const handleInstagramChange = (value: string) => {
    // Remove any URL portion if they paste a full URL
    const handle = value
      .replace(/^https?:\/\/(www\.)?instagram\.com\//, '')
      .replace(/\/$/, '');

    // Don't add @ prefix during typing - let them type naturally
    onUpdate({ instagram: handle });
  };

  const handleBioChange = (value: string) => {
    setBioLength(value.length);
    onUpdate({ bio: value });
  };

  const handleExperienceChange = (value: string) => {
    setExperienceLength(value.length);
    onUpdate({ experience: value });
  };

  const handleCameraEquipmentChange = (value: string) => {
    setCameraEquipmentLength(value.length);
    onUpdate({ cameraEquipment: value });
  };

  const handleReferenceChange = (index: 0 | 1, field: keyof Reference, value: string) => {
    const newReferences = [...formState.references] as [Reference, Reference];
    newReferences[index] = { ...newReferences[index], [field]: value };
    onUpdate({ references: newReferences });
  };

  const handleUsernameChange = (value: string) => {
    // Normalize to lowercase and remove invalid characters
    const normalized = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    onUpdate({ username: normalized });
  };

  const canProceed =
    formState.name.trim().length > 0 &&
    formState.username.trim().length >= 3 &&
    usernameAvailable !== false &&
    formState.photos.length >= REQUIRED_MODEL_PHOTOS &&
    formState.modelingStyles.length > 0 &&
    Object.keys(errors).length === 0;

  const toggleModelingStyle = (style: ModelingStyle) => {
    const newStyles = formState.modelingStyles.includes(style)
      ? formState.modelingStyles.filter(s => s !== style)
      : [...formState.modelingStyles, style];
    onUpdate({ modelingStyles: newStyles });
  };

  const selectedLanguages = formState.languages
    .map(code => LANGUAGE_OPTIONS.find(([languageCode]) => languageCode === code))
    .filter((language): language is [LanguageCode, string] => Boolean(language));
  const languageQuery = languageSearch.trim().toLowerCase();
  const availableLanguages = LANGUAGE_OPTIONS
    .filter(([code, name]) => (
      !formState.languages.includes(code) &&
      (
        !languageQuery ||
        name.toLowerCase().includes(languageQuery) ||
        code.toLowerCase().includes(languageQuery)
      )
    ))
    .slice(0, 8);

  const addLanguage = (language: LanguageCode) => {
    if (formState.languages.includes(language)) return;
    onUpdate({ languages: [...formState.languages, language] });
    setLanguageSearch('');
  };

  const removeLanguage = (language: LanguageCode) => {
    onUpdate({ languages: formState.languages.filter(code => code !== language) });
  };

  return (
    <div className="flex-1 overflow-auto bg-bg">
      <form className="form-page" onSubmit={(e) => e.preventDefault()}>
        {/* Header */}
        <div className="form-page-head">
          <div className="form-eyebrow">Model registration</div>
          <h1 className="form-heading">Create your profile</h1>
          <p className="form-lede max-w-none sm:whitespace-nowrap">
            Applications are reviewed regularly.{' '}
            <button
              type="button"
              onClick={() => setShowLifeModelInfo(true)}
              className="inline whitespace-nowrap underline"
            >
              You can be a life model
            </button>
          </p>
        </div>
        <LifeModelInfoModal
          isOpen={showLifeModelInfo}
          onClose={() => setShowLifeModelInfo(false)}
        />

        {/* Step 1: Public Profile */}
        {step === 1 && (
          <>
            <fieldset className="form-fieldset">
              <legend className="form-legend">Public profile</legend>

              <div className="form-row">
                <label className="form-field">
                  <span className="form-field-label">Your name</span>
                  <input
                    type="text"
                    value={formState.name}
                    onChange={(e) => onUpdate({ name: e.target.value })}
                    placeholder="Your name or stage name"
                    className={`form-input ${errors.name ? 'error' : ''}`}
                    autoFocus
                  />
                  {errors.name && (
                    <p className="form-field-hint" style={{ color: '#c53030', fontStyle: 'normal' }}>{errors.name}</p>
                  )}
                </label>

                <label className="form-field">
                  <span className="form-field-label">
                    Location
                    <span className="opt">optional</span>
                  </span>
                  <div className="form-select-wrap">
                    <select
                      value={formState.country}
                      onChange={(e) => onUpdate({ country: e.target.value })}
                      className="form-input form-select"
                    >
                      <option value="">Select your country</option>
                      {COUNTRIES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
              </div>

              <label className="form-field">
                <span className="form-field-label">Username</span>
                <div className="form-prefix-wrap">
                  <span className="form-prefix">lifedrawing.live/u/</span>
                  <input
                    type="text"
                    value={formState.username}
                    onChange={(e) => handleUsernameChange(e.target.value)}
                    placeholder="yourname"
                    className={errors.username ? 'error' : ''}
                    maxLength={30}
                  />
                </div>
                <p className="form-field-hint">
                  {checkingUsername && 'Checking availability...'}
                  {!checkingUsername && usernameAvailable === true && formState.username.length >= 3 && (
                    <span style={{ color: '#2a7d4f' }}>Username is available</span>
                  )}
                  {!checkingUsername && usernameAvailable === false && (
                    <span style={{ color: '#c53030' }}>Username is already taken</span>
                  )}
                  {!checkingUsername && usernameAvailable === null && formState.username.length > 0 && formState.username.length < 3 && (
                    <span>Username must be at least 3 characters</span>
                  )}
                  {!checkingUsername && usernameAvailable === null && formState.username.length === 0 && (
                    <span>This will be your public profile URL</span>
                  )}
                </p>
                {errors.username && (
                  <p className="form-field-hint" style={{ color: '#c53030', fontStyle: 'normal' }}>{errors.username}</p>
                )}
              </label>

              <div className="form-field">
                <PhotoUploader
                  photos={formState.photos}
                  onChange={handlePhotosChange}
                  maxPhotos={20}
                  minLongEdge={MODEL_PHOTO_MIN_LONG_EDGE}
                  minShortEdge={MODEL_PHOTO_MIN_SHORT_EDGE}
                  emptyLead={`Add at least ${REQUIRED_MODEL_PHOTOS} high-resolution photos of yourself.`}
                  emptySub={`Minimum ${MODEL_PHOTO_MIN_SHORT_EDGE} x ${MODEL_PHOTO_MIN_LONG_EDGE}px. You can add up to 20 photos.`}
                />
                {errors.photos && (
                  <p className="form-field-hint" style={{ color: '#c53030', fontStyle: 'normal', marginTop: '12px' }}>{errors.photos}</p>
                )}
                <p className="form-field-hint">
                  {formState.photos.length} / {REQUIRED_MODEL_PHOTOS} required. Low-resolution images are rejected before upload.
                </p>
              </div>

              <div className="form-field">
                <span className="form-field-label">Modeling setup photo</span>
                <PhotoUploader
                  photos={formState.spacePhotos}
                  onChange={handleSpacePhotosChange}
                  maxPhotos={10}
                  minLongEdge={SPACE_PHOTO_MIN_LONG_EDGE}
                  minShortEdge={SPACE_PHOTO_MIN_SHORT_EDGE}
                  emptyLead="Add one photo of your modeling setup."
                  emptySub={`Minimum ${SPACE_PHOTO_MIN_SHORT_EDGE} x ${SPACE_PHOTO_MIN_LONG_EDGE}px. Show your lighting, floor area, background, and usual camera angle.`}
                />
                <p className="form-field-hint">
                  {formState.spacePhotos.length} / {REQUIRED_SPACE_PHOTOS} required. This helps organizers understand where you will pose.
                </p>
                {errors.spacePhotos && (
                  <p className="form-field-hint" style={{ color: '#c53030', fontStyle: 'normal', marginTop: '8px' }}>{errors.spacePhotos}</p>
                )}
              </div>

              <div className="form-field">
                <span className="form-field-label">What type of modeling are you available for?</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  {MODELING_STYLE_OPTIONS.map(option => (
                    <label key={option.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formState.modelingStyles.includes(option.value)}
                        onChange={() => toggleModelingStyle(option.value)}
                        style={{ marginTop: '4px' }}
                      />
                      <span style={{ fontWeight: 500 }}>{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.modelingPrefs && (
                  <p className="form-field-hint" style={{ color: '#c53030', fontStyle: 'normal', marginTop: '8px' }}>{errors.modelingPrefs}</p>
                )}
              </div>

              <div className="form-field">
                <span className="form-field-label">
                  Languages you can conduct sessions in
                  <span className="opt">optional</span>
                </span>
                <div className="space-y-3">
                  {selectedLanguages.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedLanguages.map(([code, name]) => (
                        <span
                          key={code}
                          className="inline-flex items-center gap-2 rounded-full border border-line bg-panel px-3 py-1.5 text-[14px] text-text"
                        >
                          {name}
                          <button
                            type="button"
                            onClick={() => removeLanguage(code)}
                            className="text-text-faint transition-colors hover:text-text"
                            aria-label={`Remove ${name}`}
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <input
                    type="text"
                    value={languageSearch}
                    onChange={(event) => setLanguageSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        const firstMatch = availableLanguages[0]?.[0];
                        if (firstMatch) addLanguage(firstMatch);
                      }

                      if (event.key === 'Backspace' && !languageSearch && formState.languages.length > 0) {
                        event.preventDefault();
                        const lastLanguage = formState.languages[formState.languages.length - 1];
                        if (lastLanguage) removeLanguage(lastLanguage);
                      }
                    }}
                    className="form-input"
                    placeholder="Search languages to add"
                    aria-describedby="language-search-hint"
                  />

                  {availableLanguages.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {availableLanguages.map(([code, name]) => (
                        <button
                          key={code}
                          type="button"
                          onClick={() => addLanguage(code)}
                          className="rounded-full border border-line px-3 py-1.5 text-[14px] text-text-dim transition-colors hover:border-text/30 hover:text-text"
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  )}

                  {languageQuery && availableLanguages.length === 0 && (
                    <p className="form-field-hint">No matching languages found.</p>
                  )}
                  <p id="language-search-hint" className="form-field-hint">
                    Type a language and press Enter, or choose one from the suggestions.
                  </p>
                </div>
              </div>

              <label className="form-field">
                <span className="form-field-label">
                  Instagram
                  <span className="opt">optional</span>
                </span>
                <div className="form-prefix-wrap">
                  <span className="form-prefix">@</span>
                  <input
                    type="text"
                    value={formState.instagram}
                    onChange={(e) => handleInstagramChange(e.target.value)}
                    placeholder="yourhandle"
                  />
                </div>
                {errors.instagram && (
                  <p className="form-field-hint" style={{ color: '#c53030', fontStyle: 'normal' }}>{errors.instagram}</p>
                )}
              </label>

              <label className="form-field">
                <span className="form-field-label">
                  Website
                  <span className="opt">optional</span>
                </span>
                <input
                  type="url"
                  value={formState.website}
                  onChange={(e) => onUpdate({ website: e.target.value })}
                  placeholder="https://yourwebsite.com"
                  className={`form-input ${errors.website ? 'error' : ''}`}
                />
                {errors.website && (
                  <p className="form-field-hint" style={{ color: '#c53030', fontStyle: 'normal' }}>{errors.website}</p>
                )}
              </label>

              <label className="form-field">
                <span className="form-field-label">
                  About you
                  <span className="opt">optional</span>
                </span>
                <textarea
                  value={formState.bio}
                  onChange={(e) => handleBioChange(e.target.value)}
                  placeholder="Your experience level, preferred pose styles, or anything else you'd like artists to know..."
                  className="form-input form-textarea"
                  maxLength={500}
                />
                <div className="form-counter">
                  <span></span>
                  <span className="count">{bioLength} / 500</span>
                </div>
              </label>
            </fieldset>

            {/* Step 1 Actions */}
            <div className="form-actions">
              <div className="form-actions-left"></div>
              <div className="form-actions-right">
                <button
                  type="button"
                  onClick={onCancel}
                  className="form-btn ghost"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="form-btn solid"
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step 2: Verification */}
        {step === 2 && (
          <>
            <fieldset className="form-fieldset">
              <legend className="form-legend">Model setup</legend>
              <p className="form-legend-sub">
                Add the setup details hosts need before they book you. Prior modeling experience is helpful, but it is not required.
              </p>

              <label className="form-field">
                <span className="form-field-label">
                  Camera & equipment
                  <span className="opt">optional</span>
                </span>
                <textarea
                  value={formState.cameraEquipment}
                  onChange={(e) => handleCameraEquipmentChange(e.target.value)}
                  placeholder="Camera, lighting, background, posing area, or anything hosts should know..."
                  className="form-input form-textarea"
                  maxLength={500}
                />
                <div className="form-counter">
                  <span></span>
                  <span className="count">{cameraEquipmentLength} / 500</span>
                </div>
              </label>

              <label className="form-field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: '12px' }}>
                <input
                  type="checkbox"
                  checked={formState.defaultShowIdentity}
                  onChange={(event) => onUpdate({ defaultShowIdentity: event.target.checked })}
                  style={{ marginTop: '4px' }}
                />
                <span>
                  <span className="form-field-label" style={{ marginBottom: 0 }}>Show my model identity by default</span>
                  <span className="form-field-hint" style={{ display: 'block' }}>
                    You can override this per event later.
                  </span>
                </span>
              </label>

              <label className="form-field">
                <span className="form-field-label">
                  Artwork of you
                  <span className="opt">optional</span>
                </span>
                <PhotoUploader
                  photos={formState.verificationPhotos}
                  onChange={handleVerificationPhotosChange}
                  maxPhotos={4}
                />
                <p className="form-field-hint">Images that artists have drawn or painted of you in past sessions.</p>
              </label>

              <label className="form-field">
                <span className="form-field-label">
                  Recent experience
                  <span className="opt">optional</span>
                </span>
                <textarea
                  value={formState.experience}
                  onChange={(e) => handleExperienceChange(e.target.value)}
                  placeholder="Where have you modeled recently? Any studios, schools, or private sessions..."
                  className="form-input form-textarea"
                  maxLength={500}
                />
                <div className="form-counter">
                  <span></span>
                  <span className="count">{experienceLength} / 500</span>
                </div>
              </label>

              {/* Reference 1 */}
              <div className="form-field" style={{ marginBottom: '32px' }}>
                <span className="form-field-label">
                  Reference 1
                  <span className="opt">optional</span>
                </span>
                <div className="form-row" style={{ marginTop: '8px' }}>
                  <input
                    type="text"
                    value={formState.references[0].name}
                    onChange={(e) => handleReferenceChange(0, 'name', e.target.value)}
                    placeholder="Name"
                    className="form-input"
                  />
                  <input
                    type="email"
                    value={formState.references[0].email}
                    onChange={(e) => handleReferenceChange(0, 'email', e.target.value)}
                    placeholder="Email"
                    className="form-input"
                  />
                </div>
                <input
                  type="url"
                  value={formState.references[0].website}
                  onChange={(e) => handleReferenceChange(0, 'website', e.target.value)}
                  placeholder="Organization website"
                  className="form-input"
                  style={{ marginTop: '12px' }}
                />
              </div>

              {/* Reference 2 */}
              <div className="form-field">
                <span className="form-field-label">
                  Reference 2
                  <span className="opt">optional</span>
                </span>
                <div className="form-row" style={{ marginTop: '8px' }}>
                  <input
                    type="text"
                    value={formState.references[1].name}
                    onChange={(e) => handleReferenceChange(1, 'name', e.target.value)}
                    placeholder="Name"
                    className="form-input"
                  />
                  <input
                    type="email"
                    value={formState.references[1].email}
                    onChange={(e) => handleReferenceChange(1, 'email', e.target.value)}
                    placeholder="Email"
                    className="form-input"
                  />
                </div>
                <input
                  type="url"
                  value={formState.references[1].website}
                  onChange={(e) => handleReferenceChange(1, 'website', e.target.value)}
                  placeholder="Organization website"
                  className="form-input"
                  style={{ marginTop: '12px' }}
                />
              </div>
            </fieldset>

            {/* Step 2 Actions */}
            <div className="form-actions">
              <div className="form-actions-left"></div>
              <div className="form-actions-right">
                <button
                  type="button"
                  onClick={handleBack}
                  className="form-btn ghost"
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="form-btn solid"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>Submit application →</>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
